import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Upload, Trash2, Plus, ExternalLink, Eye, EyeOff } from "lucide-react";

const MAX_ACTIVE = 6;
const EMPTY = { name: "", logo_file_id: null, logo_url: "", link_url: "", alt: "", position: 0, active: true };

export default function SponsorsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sponsors");
      setItems(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return null;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Format invalide. Utilisez JPG, PNG ou WEBP.");
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Fichier trop volumineux (max 5 Mo).");
      return null;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "sponsor_logo");
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return { id: data.id, url: data.url };
    } catch (e) {
      setError(e?.response?.data?.detail || "Échec de l'envoi");
      return null;
    } finally { setUploading(false); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const res = await upload(f);
    if (res) setDraft((d) => ({ ...d, logo_file_id: res.id, logo_url: res.url }));
    e.target.value = "";
  };

  const create = async () => {
    if (!draft.name.trim() || !draft.logo_file_id) {
      setError("Nom et logo requis.");
      return;
    }
    setError("");
    try {
      await api.post("/admin/sponsors", {
        name: draft.name.trim(),
        logo_file_id: draft.logo_file_id,
        link_url: draft.link_url.trim() || null,
        alt: draft.alt.trim() || draft.name.trim(),
        position: draft.position || 0,
        active: draft.active,
      });
      setDraft(EMPTY);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur");
    }
  };

  const updateOne = async (s, patch) => {
    setError("");
    try {
      await api.put(`/admin/sponsors/${s.id}`, {
        name: patch.name ?? s.name,
        logo_file_id: patch.logo_file_id ?? (s.logo_url ? s.logo_url.split("/").pop() : null),
        link_url: patch.link_url ?? s.link_url ?? null,
        alt: patch.alt ?? s.alt,
        position: patch.position ?? s.position,
        active: patch.active ?? s.active,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce sponsor définitivement ?")) return;
    await api.delete(`/admin/sponsors/${id}`);
    await load();
  };

  const activeCount = items.filter((s) => s.active).length;

  return (
    <div className="p-8 lg:p-12" data-testid="sponsors-admin">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-2">Sponsors</h1>
      <p className="text-[#A8A39D] text-sm mb-10 max-w-2xl">
        Bandeau « Avec le soutien de » affiché sur les pages publiques. Maximum {MAX_ACTIVE} sponsors actifs simultanément.
        Logo recommandé : 400×200, fond transparent (WEBP/PNG).
      </p>

      <div className="stat-card mb-10" data-testid="sponsor-form">
        <div className="overline mb-6 flex items-center gap-3">
          <Plus size={14} /> Ajouter un sponsor
          <span className="ml-auto text-[#A8A39D] text-[10px]">
            {activeCount} / {MAX_ACTIVE} actifs
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline text-xs">Nom du sponsor</label>
            <input data-testid="new-sponsor-name" className="luxe-input mt-2" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Ex : Café Saint-Henri" />
          </div>
          <div>
            <label className="overline text-xs">Lien (optionnel)</label>
            <input data-testid="new-sponsor-link" className="luxe-input mt-2" value={draft.link_url} onChange={(e) => setDraft({ ...draft, link_url: e.target.value })} placeholder="https://..." />
          </div>
          <div>
            <label className="overline text-xs">Texte alt</label>
            <input data-testid="new-sponsor-alt" className="luxe-input mt-2" value={draft.alt} onChange={(e) => setDraft({ ...draft, alt: e.target.value })} placeholder="Description pour accessibilité" />
          </div>
          <div>
            <label className="overline text-xs">Position d'affichage</label>
            <input data-testid="new-sponsor-position" type="number" className="luxe-input mt-2" value={draft.position} onChange={(e) => setDraft({ ...draft, position: parseInt(e.target.value) || 0 })} />
          </div>
        </div>
        <div className="mt-6 flex items-center gap-6">
          <button
            data-testid="new-sponsor-upload"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="btn-ghost"
          >
            <Upload size={14} />
            {uploading ? "Envoi..." : draft.logo_file_id ? "Changer le logo" : "Téléverser le logo"}
          </button>
          <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickFile} className="hidden" />
          {draft.logo_url && (
            <img src={draft.logo_url} alt="" className="h-12 w-auto object-contain bg-white/5 px-3 py-1" />
          )}
        </div>
        {error && <div data-testid="sponsor-error" className="mt-4 text-red-400 text-sm">{error}</div>}
        <div className="mt-6">
          <button data-testid="new-sponsor-submit" onClick={create} disabled={!draft.name || !draft.logo_file_id} className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed">
            <Plus size={14} /> Créer le sponsor
          </button>
        </div>
      </div>

      <div className="stat-card" data-testid="sponsors-list">
        <div className="overline mb-6">Sponsors enregistrés ({items.length})</div>
        {loading && <div className="text-[#A8A39D] text-sm">Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="text-[#A8A39D] text-sm py-6">Aucun sponsor enregistré.</div>
        )}
        {!loading && items.map((s) => (
          <div key={s.id} data-testid={`sponsor-row-${s.id}`} className="py-4 border-b border-white/5 flex items-center gap-6">
            <div className="w-28 h-14 bg-[#141210] border border-white/5 flex items-center justify-center shrink-0">
              {s.logo_url ? <img src={s.logo_url} alt={s.alt} className="max-w-full max-h-full object-contain" /> : <div className="text-[10px] text-[#666]">Pas de logo</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg truncate">{s.name}</div>
              <div className="text-xs text-[#A8A39D] truncate">
                {s.link_url ? (
                  <a href={s.link_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#C5A059] inline-flex items-center gap-1">
                    <ExternalLink size={11} /> {s.link_url}
                  </a>
                ) : "Pas de lien"}
                <span className="ml-3">· Position {s.position}</span>
              </div>
            </div>
            <button
              data-testid={`sponsor-toggle-${s.id}`}
              onClick={() => updateOne(s, { active: !s.active })}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border transition-colors ${
                s.active ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-white/10 text-[#A8A39D] hover:text-[#F5F0EB]"
              }`}
            >
              {s.active ? <><Eye size={11} className="inline mr-1" /> Actif</> : <><EyeOff size={11} className="inline mr-1" /> Inactif</>}
            </button>
            <button
              data-testid={`sponsor-delete-${s.id}`}
              onClick={() => remove(s.id)}
              className="text-red-400 hover:text-red-300 p-2"
              title="Supprimer"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
